import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Job } from "@/lib/schema";
import { formatDisplayText, formatRewardAmount, formatDateForSouthAfrica } from "@/lib/textUtils";
import { 
  getMatchScore
} from "@/lib/jobDetailsUtils";
import { AdminReferralWithUI } from "@/api/referrals";
import { ReferralTableRow } from "@/components/referrals/ReferralTableRow";
import { JobDescription } from "./JobDescription";
import { JobSkills } from "./JobSkills";

interface JobDetailsTabsProps {
  job: Job;
  referrals: AdminReferralWithUI[];
  referralsLoading: boolean;
  onViewReferral?: (referral: AdminReferralWithUI) => void;
}

export function JobDetailsTabs({ 
  job, 
  referrals, 
  referralsLoading,
  onViewReferral
}: JobDetailsTabsProps) {
  const [activeTab, setActiveTab] = useState("referrals");

  // Sort referrals to show hired candidates first when job status is filled
  const sortedReferrals = job.status === 'filled' 
    ? [...referrals].sort((a, b) => {
        if (a.status === 'hired' && b.status !== 'hired') return -1;
        if (a.status !== 'hired' && b.status === 'hired') return 1;
        return 0;
      })
    : referrals;

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
      <TabsList className="grid w-full grid-cols-2 lg:w-auto lg:grid-cols-2">
        <TabsTrigger value="referrals" data-testid="tab-referrals">
          Referrals ({referrals.length})
        </TabsTrigger>
        <TabsTrigger value="overview" data-testid="tab-overview">Job Overview</TabsTrigger>
      </TabsList>

      <TabsContent value="overview" className="space-y-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Job Information */}
          <Card className="border-0 shadow-sm">
            <CardHeader>
              <CardTitle className="text-lg">Job Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Job ID</label>
                  <p className="text-sm font-medium text-gray-900 mt-1">{job.internalJobId}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Employment Type</label>
                  <p className="text-sm font-medium text-gray-900 mt-1">{formatDisplayText(job.employmentType)}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Category</label>
                  <p className="text-sm font-medium text-gray-900 mt-1">{formatDisplayText(job.category)}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Created Date</label>
                  <p className="text-sm font-medium text-gray-900 mt-1">
                    {formatDateForSouthAfrica(job.createdAt)}
                  </p>
                </div>
              </div>
              {job.salary && (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Salary</label>
                  <p className="text-sm font-medium text-gray-900 mt-1">R{formatRewardAmount(job.salary)}</p>
                </div>
              )}
              {job.rewardInAirPoints && (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Reward</label>
                  <p className="text-sm font-medium mt-1">
                    R{formatRewardAmount(job.rewardInAirPoints)}
                  </p>
                </div>
              )}
              <div>
                <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">Assigned Recruiter</label>
                <p className="text-sm font-medium text-gray-900 mt-1">
                  {job.assignedRecruiter?.name || 'Unassigned'}
                </p>
              </div>
              {job.importantNotes && (
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Important Notes
                  </label>
                  <p className="text-sm text-gray-900 mt-1 whitespace-pre-line">
                    {job.importantNotes}
                  </p>
                </div>
              )}
              <JobSkills skills={job.skills} />
            </CardContent>
          </Card>

          {/* Job Description */}
          <Card className="border-0 shadow-sm">
            <CardContent>
              <JobDescription 
                description={job.description}
                showTitle={false}
              />
            </CardContent>
          </Card>
        </div>
      </TabsContent>

      <TabsContent value="referrals" className="space-y-6">
        <Card className="border-0 shadow-sm">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-slate-200">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider space-grotesk">
                      Candidate
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider space-grotesk">
                      Referrer
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider space-grotesk">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider space-grotesk">
                      Match Score
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider space-grotesk">
                      Recommendation
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider space-grotesk">
                      Submitted
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider space-grotesk">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-200">
                  {referralsLoading ? (
                    // Loading skeleton for referrals
                    Array.from({ length: 3 }).map((_, index) => (
                      <tr key={`loading-${index}`} className="hover:bg-slate-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <Skeleton className="h-10 w-10 rounded-full" />
                            <div>
                              <Skeleton className="h-4 w-32 mb-1" />
                              <Skeleton className="h-3 w-40" />
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap"><Skeleton className="h-4 w-24" /></td>
                        <td className="px-6 py-4 whitespace-nowrap"><Skeleton className="h-6 w-20" /></td>
                        <td className="px-6 py-4 whitespace-nowrap"><Skeleton className="h-4 w-16" /></td>
                        <td className="px-6 py-4 whitespace-nowrap"><Skeleton className="h-6 w-24" /></td>
                        <td className="px-6 py-4 whitespace-nowrap"><Skeleton className="h-4 w-20" /></td>
                        <td className="px-6 py-4 whitespace-nowrap"><Skeleton className="h-8 w-16" /></td>
                      </tr>
                    ))
                  ) : (
                    sortedReferrals.map((referral) => (
                      <ReferralTableRow
                        key={referral.id}
                        referral={{
                          id: referral.id,
                          candidateName: referral.candidateName,
                          candidateEmail: referral.candidateEmail,
                          referrerName: referral.referrerName,
                          status: referral.status,
                          matchingScore: getMatchScore(referral) || 0,
                          recommendation: referral.recommendation,
                          createdAt: referral.createdAt
                        }}
                        onViewDetails={(referralId) => {
                          // Find the full referral object (already AdminReferralWithUI)
                          const fullReferral = referrals.find(r => r.id === referralId);
                          if (fullReferral && onViewReferral) {
                            onViewReferral(fullReferral);
                          } else {
                            console.log('View referral details for:', referralId);
                          }
                        }}
                        showActions={true}
                        showMatchingScore={true}
                        showRecommendation={true}
                      />
                    ))
                  )}
                </tbody>
              </table>
            </div>
            
            {!referralsLoading && sortedReferrals.length === 0 && (
              <div className="text-center py-8">
                <p className="text-slate-500 space-grotesk">No referrals found</p>
              </div>
            )}
          </CardContent>
        </Card>
      </TabsContent>

    </Tabs>
  );
}
